import React, { useState, useEffect, useRef } from 'react';
import { MessageCircle } from 'react-feather';
import { Button, Form, Modal } from 'react-bootstrap';
import Swal from 'sweetalert2';
import { useMsal } from '@azure/msal-react';
import { APP_VERSION } from '../constants';
import { buildUrl, fetchWithAccessToken } from '../services/HttpService';
import { getEnvVariable } from '../utils/generalUtils';
import { getAccessToken, getTenantName, parseJwt } from '../utils/tokenUtils';

/** Normalize to UUID string when possible (matches API / DB type for submitted_by_user_id). */
function normalizeUuid(value) {
    if (value == null) return '';
    const raw = String(value).trim().replace(/^\{|\}$/g, '');
    const uuidV1toV5Regex = /^[0-9a-f]{8}-[0-9a-f]{4}-[1-5][0-9a-f]{3}-[89ab][0-9a-f]{3}-[0-9a-f]{12}$/i;
    if (uuidV1toV5Regex.test(raw)) return raw;
    // Some providers emit 32-char hex without dashes.
    if (/^[0-9a-f]{32}$/i.test(raw)) {
        const hyphenated = `${raw.slice(0, 8)}-${raw.slice(8, 12)}-${raw.slice(12, 16)}-${raw.slice(16, 20)}-${raw.slice(20)}`;
        if (uuidV1toV5Regex.test(hyphenated)) return hyphenated;
    }
    return '';
}

function getFeedbackSubmitPath() {
    return (getEnvVariable('VITE_APP_FEEDBACK_SUBMIT_PATH') || '').trim();
}

/**
 * Resolves the POST URL for feedback.
 * - Relative path (default): one or more segments for {@link buildUrl}, e.g. `beta-feedback` → `{VITE_APP_API_HOST_URL}/api/{version}/beta-feedback`.
 * - Absolute URL: if the value starts with `http://` or `https://`, it is used as-is (e.g. auth API on another host: `http://127.0.0.1:5555/auth/betafeedback`).
 */
function resolveFeedbackSubmitUrl(path) {
    const p = (path || '').trim();
    if (!p) return null;
    const lower = p.toLowerCase();
    if (lower.startsWith('http://') || lower.startsWith('https://')) {
        return p;
    }
    return buildUrl(p);
}

/**
 * Footer and other callers use this to decide whether to show the feedback control.
 * True when VITE_APP_FEEDBACK_ENABLED is "true", or when VITE_APP_FEEDBACK_SUBMIT_PATH is non-empty.
 */
function isFeedbackEnabled() {
    const flag = getEnvVariable('VITE_APP_FEEDBACK_ENABLED');
    if (flag === 'true') return true;
    return getFeedbackSubmitPath().length > 0;
}

/**
 * Snapshot of browser/app context at call time (call on submit so viewport matches send moment).
 */
function collectFeedbackContext() {
    if (typeof window === 'undefined') {
        return {
            href: '',
            pathname: '',
            search: '',
            queryParams: {},
            viewport: {},
            screen: {},
            userAgent: '',
            referrer: '',
            appVersion: APP_VERSION,
        };
    }

    const search = window.location.search || '';
    let queryParams = {};
    try {
        queryParams = Object.fromEntries(new URLSearchParams(search));
    } catch {
        queryParams = {};
    }

    return {
        href: window.location.href,
        pathname: window.location.pathname,
        search,
        queryParams,
        viewport: {
            innerWidth: window.innerWidth,
            innerHeight: window.innerHeight,
        },
        screen: {
            width: window.screen?.width ?? null,
            height: window.screen?.height ?? null,
        },
        userAgent: typeof navigator !== 'undefined' ? navigator.userAgent : '',
        referrer: typeof document !== 'undefined' ? document.referrer || '' : '',
        appVersion: APP_VERSION,
        buildDate: typeof __BUILD_DATE__ !== 'undefined' ? __BUILD_DATE__ : undefined,
    };
}

/**
 * User id for feedback POST body.
 * Prefer MSAL active account (same source as profile OID) — the Data API access token is a
 * separate JWT from BADDOG handoff and often lacks Entra `oid` / UUID-shaped claims.
 */
function getSubmittedByUserId(msalActiveAccount) {
    if (msalActiveAccount) {
        const fromMsal =
            /* Profile page labels localAccountId as OID */ normalizeUuid(msalActiveAccount.localAccountId) ||
            normalizeUuid(msalActiveAccount.idTokenClaims?.oid);
        if (fromMsal) return fromMsal;
    }

    const token = getAccessToken();
    if (!token) return '00000000-0000-0000-0000-000000000000';
    const d = parseJwt(token);
    if (!d || typeof d !== 'object') return '00000000-0000-0000-0000-000000000000';
    const candidates = [
        d.submitted_by_user_id,
        d.submittedByUserId,
        d.user_id,
        d.userId,
        d.telescope_user_id,
        d.telescopeUserId,
        d.oid,
        d?.authorities?.[0]?.authority?.userid,
        d.sub,
    ];
    for (const candidate of candidates) {
        const normalized = normalizeUuid(candidate);
        if (normalized) return normalized;
    }
    return '00000000-0000-0000-0000-000000000000';
}

/**
 * Builds plain-text `message` and structured `context_extra` from user bug-report fields.
 * @param {{ whatHappened: string, whatExpected: string, howToReproduce: string, issueRank: number }} fields
 * @param {import('@azure/msal-browser').AccountInfo | null} [msalActiveAccount]
 */
function buildFeedbackApiPayload(fields, msalActiveAccount = null) {
    const whatHappened = (fields.whatHappened || '').trim();
    const whatExpected = (fields.whatExpected || '').trim();
    const howToReproduce = (fields.howToReproduce || '').trim();
    const rawRank = fields.issueRank != null ? Number(fields.issueRank) : 5;
    const issueRank = Number.isFinite(rawRank)
        ? Math.min(10, Math.max(1, Math.round(rawRank)))
        : 5;

    const message = [
        whatHappened && `What happened:\n${whatHappened}`,
        whatExpected && `What I expected:\n${whatExpected}`,
        howToReproduce && `How to reproduce:\n${howToReproduce}`,
        `How I'd rank this issue (1 = low, 10 = critical): ${issueRank}`,
    ]
        .filter(Boolean)
        .join('\n\n');

    const c = collectFeedbackContext();
    return {
        submitted_by_user_id: getSubmittedByUserId(msalActiveAccount),
        message,
        page_href: c.href,
        page_pathname: c.pathname,
        search: c.search,
        query_params: c.queryParams && typeof c.queryParams === 'object' ? c.queryParams : {},
        viewport_width: Number.isFinite(c.viewport?.innerWidth) ? c.viewport.innerWidth : 0,
        viewport_height: Number.isFinite(c.viewport?.innerHeight) ? c.viewport.innerHeight : 0,
        screen_width: c.screen?.width != null ? Number(c.screen.width) : 0,
        screen_height: c.screen?.height != null ? Number(c.screen.height) : 0,
        user_agent: c.userAgent || '',
        referrer: c.referrer || '',
        app_version: c.appVersion || '',
        ...(c.buildDate ? { build_date: c.buildDate } : {}),
        context_extra: {
            what_happened: whatHappened,
            what_i_expected: whatExpected,
            how_to_reproduce: howToReproduce,
            issue_rank: issueRank,
            tenant_name: getTenantName() || '',
        },
    };
}

/**
 * Opens a modal to capture beta feedback and POST JSON to the API (when configured).
 *
 * @param {React.ReactNode} [children] - Trigger content. Default: icon + "Send feedback".
 * @param {string} [className] - Classes for the trigger button (link-like in footer by default).
 */
const FeedbackLink = ({
    children,
    className = 'p-0 border-0 bg-transparent text-body text-decoration-underline',
}) => {
    const { instance } = useMsal();
    const [show, setShow] = useState(false);
    const [whatHappened, setWhatHappened] = useState('');
    const [whatExpected, setWhatExpected] = useState('');
    const [howToReproduce, setHowToReproduce] = useState('');
    const [issueRank, setIssueRank] = useState(5);
    const [submitError, setSubmitError] = useState('');
    const [isSubmitting, setIsSubmitting] = useState(false);
    const firstFieldRef = useRef(null);

    const handleClose = () => {
        setShow(false);
        setWhatHappened('');
        setWhatExpected('');
        setHowToReproduce('');
        setIssueRank(5);
        setSubmitError('');
    };

    useEffect(() => {
        if (show && firstFieldRef.current) {
            const t = setTimeout(() => firstFieldRef.current?.focus(), 100);
            return () => clearTimeout(t);
        }
    }, [show]);

    const handleSubmit = async (e) => {
        e.preventDefault();
        const missing = [];
        if (!whatHappened.trim()) missing.push('What happened');
        if (missing.length > 0) {
            setSubmitError(`Please fill in: ${missing.join(', ')}.`);
            return;
        }
        setSubmitError('');

        const path = getFeedbackSubmitPath();
        if (!path) {
            Swal.fire({
                icon: 'info',
                title: 'Feedback',
                text: 'The feedback service is not configured yet.',
                customClass: { confirmButton: 'btn btn-primary' },
                buttonsStyling: false,
            });
            return;
        }

        setIsSubmitting(true);
        try {
            const url = resolveFeedbackSubmitUrl(path);
            if (!url) {
                setSubmitError('Invalid feedback URL configuration.');
                return;
            }
            const payload = buildFeedbackApiPayload(
                {
                    whatHappened,
                    whatExpected,
                    howToReproduce,
                    issueRank,
                },
                instance.getActiveAccount()
            );
            const response = await fetchWithAccessToken(url, {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify(payload),
            });

            if (!response || !response.ok) {
                setSubmitError('Could not send feedback. Please try again later.');
                return;
            }

            handleClose();
            Swal.fire({
                icon: 'success',
                title: 'Thank you',
                text: 'Your feedback was submitted.',
                customClass: { confirmButton: 'btn btn-primary' },
                buttonsStyling: false,
            });
        } catch (err) {
            setSubmitError(err?.message || 'Something went wrong.');
        } finally {
            setIsSubmitting(false);
        }
    };

    const defaultContent = (
        <>
            <MessageCircle size={14} className="me-1" style={{ verticalAlign: 'middle' }} />
            <span>Send feedback</span>
        </>
    );

    return (
        <>
            <button type="button" className={className} onClick={() => setShow(true)}>
                {children != null ? children : defaultContent}
            </button>

            <Modal show={show} onHide={handleClose} centered size="lg">
                <Modal.Header closeButton>
                    <Modal.Title className="fs-6">Send feedback</Modal.Title>
                </Modal.Header>
                <Form onSubmit={handleSubmit}>
                    <Modal.Body>
                        <Form.Group className="mb-3">
                            <Form.Label>What happened? <span className="text-danger">*</span></Form.Label>
                            <Form.Control
                                ref={firstFieldRef}
                                as="textarea"
                                rows={3}
                                value={whatHappened}
                                onChange={(e) => setWhatHappened(e.target.value)}
                                disabled={isSubmitting}
                                placeholder="Describe what you saw or what went wrong."
                            />
                        </Form.Group>
                        <Form.Group className="mb-3">
                            <Form.Label>What did you expect to happen?</Form.Label>
                            <Form.Control
                                as="textarea"
                                rows={3}
                                value={whatExpected}
                                onChange={(e) => setWhatExpected(e.target.value)}
                                disabled={isSubmitting}
                                placeholder="Describe the correct or expected behavior."
                            />
                        </Form.Group>
                        {/* <Form.Group className="mb-3">
                            <Form.Label>How can it be reproduced?</Form.Label>
                            <Form.Control
                                as="textarea"
                                rows={3}
                                value={howToReproduce}
                                onChange={(e) => setHowToReproduce(e.target.value)}
                                disabled={isSubmitting}
                                placeholder="Steps, data, or context that help us reproduce the issue."
                            />
                        </Form.Group> */}
                        <Form.Group className="mb-2">
                            <Form.Label className="d-flex justify-content-between align-items-center gap-2">
                                <span>How would you rank this issue?</span>
                                <span className="text-muted small fw-normal">{issueRank} / 10</span>
                            </Form.Label>
                            <Form.Text className="d-block mb-2 text-muted">
                                1 = not important to you, 10 = critical / must be addressed soon
                            </Form.Text>
                            <Form.Range
                                min={1}
                                max={10}
                                step={1}
                                value={issueRank}
                                onChange={(e) => setIssueRank(Number(e.target.value))}
                                disabled={isSubmitting}
                                aria-valuemin={1}
                                aria-valuemax={10}
                                aria-valuenow={issueRank}
                            />
                        </Form.Group>
                        {submitError ? <div className="text-danger small mt-2">{submitError}</div> : null}
                    </Modal.Body>
                    <Modal.Footer>
                        <Button variant="secondary" type="button" onClick={handleClose} disabled={isSubmitting}>
                            Cancel
                        </Button>
                        <Button variant="primary" type="submit" disabled={isSubmitting}>
                            {isSubmitting ? (
                                <>
                                    <span
                                        className="spinner-border spinner-border-sm me-2"
                                        role="status"
                                        aria-hidden="true"
                                    />
                                    Sending…
                                </>
                            ) : (
                                'Submit'
                            )}
                        </Button>
                    </Modal.Footer>
                </Form>
            </Modal>
        </>
    );
};

export default FeedbackLink;
export { isFeedbackEnabled, collectFeedbackContext, getFeedbackSubmitPath, buildFeedbackApiPayload };
